/**
 * Spatial focus / D-pad navigation foundation for TV and large-screen UIs.
 * No platform TV SDKs — pure DOM focus management.
 *
 * Usage: mark focusable controls with data-ngp-focus="id" (and optional
 * data-ngp-focus-group). Manager remembers last focused id per group.
 */

var DEFAULT_MAPPINGS = Object.freeze({
  ArrowUp: "up",
  ArrowDown: "down",
  ArrowLeft: "left",
  ArrowRight: "right",
  Enter: "select",
  " ": "select",
  Escape: "back",
  Backspace: "back",
  // Common CTV / gamepad-adjacent codes (browser-dependent)
  BrowserBack: "back",
  MediaPlayPause: "playpause",
  MediaPlay: "play",
  MediaPause: "pause",
  MediaStop: "stop",
  MediaTrackNext: "next",
  MediaTrackPrevious: "prev",
});

/**
 * @param {HTMLElement} root
 * @param {object} [options]
 * @param {object} [options.keyMap] merge/override remote mappings
 * @param {string} [options.initialFocus] focus id
 * @param {function} [options.onAction] (action, event) => boolean|void — return true to prevent default
 * @returns {object}
 */
export function createSpatialFocusManager(root, options) {
  options = options || {};
  if (!root) {
    return {
      destroy: function () {},
      focus: function () {},
      getFocusedId: function () {
        return null;
      },
      refresh: function () {},
    };
  }

  var keyMap = Object.assign({}, DEFAULT_MAPPINGS, options.keyMap || {});
  var memory = Object.create(null); // group -> focus id
  var currentId = options.initialFocus || null;
  var enabled = options.enabled !== false;

  function queryFocusables() {
    return Array.prototype.slice.call(
      root.querySelectorAll("[data-ngp-focus]")
    ).filter(function (el) {
      if (el.hasAttribute("disabled") || el.getAttribute("aria-hidden") === "true") {
        return false;
      }
      var style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden";
    });
  }

  function idOf(el) {
    return el && el.getAttribute("data-ngp-focus");
  }

  function groupOf(el) {
    return (el && el.getAttribute("data-ngp-focus-group")) || "default";
  }

  function findById(id) {
    if (!id) return null;
    var list = queryFocusables();
    for (var i = 0; i < list.length; i++) {
      if (idOf(list[i]) === id) return list[i];
    }
    return null;
  }

  function markFocused(el) {
    queryFocusables().forEach(function (node) {
      node.classList.remove("ngp-focus");
      node.removeAttribute("data-ngp-focused");
    });
    if (!el) {
      currentId = null;
      return;
    }
    el.classList.add("ngp-focus");
    el.setAttribute("data-ngp-focused", "true");
    currentId = idOf(el);
    memory[groupOf(el)] = currentId;
    try {
      el.focus({ preventScroll: false });
    } catch (e) {
      try {
        el.focus();
      } catch (e2) {
        /* ignore */
      }
    }
  }

  function center(el) {
    var r = el.getBoundingClientRect();
    return { x: r.left + r.width / 2, y: r.top + r.height / 2, el: el, r: r };
  }

  function pickSpatial(fromEl, direction) {
    var candidates = queryFocusables();
    if (!candidates.length) return null;
    if (!fromEl) return candidates[0];

    var from = center(fromEl);
    var best = null;
    var bestScore = Infinity;

    candidates.forEach(function (el) {
      if (el === fromEl) return;
      var c = center(el);
      var dx = c.x - from.x;
      var dy = c.y - from.y;
      var primary;
      var secondary;
      var aligned;

      if (direction === "left") {
        if (dx >= -2) return;
        primary = -dx;
        secondary = Math.abs(dy);
        aligned = overlapY(from.r, c.r);
      } else if (direction === "right") {
        if (dx <= 2) return;
        primary = dx;
        secondary = Math.abs(dy);
        aligned = overlapY(from.r, c.r);
      } else if (direction === "up") {
        if (dy >= -2) return;
        primary = -dy;
        secondary = Math.abs(dx);
        aligned = overlapX(from.r, c.r);
      } else if (direction === "down") {
        if (dy <= 2) return;
        primary = dy;
        secondary = Math.abs(dx);
        aligned = overlapX(from.r, c.r);
      } else {
        return;
      }

      // Prefer geometrically aligned neighbors, then nearest
      var score = primary + secondary * (aligned ? 0.15 : 1.2);
      if (score < bestScore) {
        bestScore = score;
        best = el;
      }
    });

    return best;
  }

  function overlapX(a, b) {
    return a.left < b.right && b.left < a.right;
  }

  function overlapY(a, b) {
    return a.top < b.bottom && b.top < a.bottom;
  }

  function restoreMemory() {
    var preferred = currentId;
    if (preferred && findById(preferred)) {
      markFocused(findById(preferred));
      return;
    }
    // Fall back to first in default group memory or first focusable
    var groups = Object.keys(memory);
    for (var i = 0; i < groups.length; i++) {
      var el = findById(memory[groups[i]]);
      if (el) {
        markFocused(el);
        return;
      }
    }
    var list = queryFocusables();
    if (list[0]) markFocused(list[0]);
  }

  function onKeyDown(e) {
    if (!enabled) return;
    var action = keyMap[e.key] || keyMap[e.code];
    if (!action) return;

    if (typeof options.onAction === "function") {
      var handled = options.onAction(action, e);
      if (handled === true) {
        e.preventDefault();
        return;
      }
    }

    if (
      action === "up" ||
      action === "down" ||
      action === "left" ||
      action === "right"
    ) {
      e.preventDefault();
      var from = document.activeElement;
      if (!from || !root.contains(from) || !from.hasAttribute("data-ngp-focus")) {
        from = findById(currentId) || queryFocusables()[0];
      }
      var next = pickSpatial(from, action);
      if (next) markFocused(next);
      return;
    }

    if (action === "select") {
      var focused = findById(currentId) || document.activeElement;
      if (focused && root.contains(focused) && typeof focused.click === "function") {
        // Enter on buttons — allow native activation; prevent scroll on Space
        if (e.key === " ") e.preventDefault();
      }
    }
  }

  function onFocusIn(e) {
    var t = e.target;
    if (t && t.hasAttribute && t.hasAttribute("data-ngp-focus")) {
      markFocused(t);
    }
  }

  root.addEventListener("keydown", onKeyDown);
  root.addEventListener("focusin", onFocusIn);

  // Initial focus after paint
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(restoreMemory);
  } else {
    setTimeout(restoreMemory, 0);
  }

  return {
    enable: function () {
      enabled = true;
    },
    disable: function () {
      enabled = false;
    },
    isEnabled: function () {
      return enabled;
    },
    focus: function (id) {
      var el = findById(id);
      if (el) markFocused(el);
    },
    getFocusedId: function () {
      return currentId;
    },
    getMemory: function () {
      return Object.assign({}, memory);
    },
    refresh: restoreMemory,
    setKeyMap: function (map) {
      keyMap = Object.assign({}, DEFAULT_MAPPINGS, map || {});
    },
    destroy: function () {
      root.removeEventListener("keydown", onKeyDown);
      root.removeEventListener("focusin", onFocusIn);
      queryFocusables().forEach(function (node) {
        node.classList.remove("ngp-focus");
        node.removeAttribute("data-ngp-focused");
      });
    },
  };
}

export { DEFAULT_MAPPINGS as TV_REMOTE_KEY_MAP };

export default createSpatialFocusManager;
